import React, { ReactElement, Fragment } from 'react';
import { TFunction } from 'i18next';
import { FastField, FastFieldProps, Field, useField, useFormikContext } from 'formik';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import ColorPicker from 'components/@common/ColorPicker';
import Select from 'components/@common/Inputs/Select';
import InviteFormFragment from 'components/@common/Forms/InviteFormFragment';
import PopoverWrap from 'components/@common/Popover';
import SearchOrAdd from 'components/@common/Search/SearchOrAdd';
import { StudentLoginType } from 'types/schema-generated-types';
import PopoverImg from '../../../../../assets/tooltip.png';

interface ClassroomDetailsOptionValues {
  label?: string;
  value?: number;
}

interface CoEducator {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}
export interface ClassroomDetailsFieldsProps {
  classes: { [key: string]: string };
  name: string;
  t: TFunction;
  autoFocus?: boolean;
  label?: string;
  defaultValue?: number | string;
  values?: ClassroomDetailsOptionValues[];
  disabled?: boolean;
}

const colors = [
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722'
];

export const ClassroomNameField = ({
  classes,
  name,
  t,
  disabled,
  autoFocus,
  label = 'Class Name'
}: ClassroomDetailsFieldsProps): ReactElement => (
  <FastField name={name}>
    {({ field, meta }: FastFieldProps): ReactElement[] => [
      <TextField
        {...field}
        autoFocus={autoFocus}
        key={`${field.name}_input`}
        label={t(label)}
        fullWidth
        placeholder={t(label)}
        variant="outlined"
        size="small"
        disabled={disabled}
        error={Boolean(meta.touched && meta.error)}
        helperText={meta.touched && meta.error}
        className={classes.input}
        inputProps={{
          'aria-label': t(label)
        }}
      />
    ]}
  </FastField>
);

export const OrganizationNameField = ({
  classes,
  name,
  t,
  disabled,
  label = 'Organization Name',
  values
}: ClassroomDetailsFieldsProps): ReactElement => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [organizationField, organizationMeta] = useField(name);
  const { onChange: onOrganizationFieldChange, ...restOrganizationField } = organizationField;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (+event.target.value !== organizationField.value) {
      setFieldValue('educator', '');
      setFieldValue('coeducators', []);
      setFieldValue('schoolYear', '');
      setFieldTouched('educator');
      setFieldTouched('coeducators');
      setFieldTouched('schoolYear');
    }

    onOrganizationFieldChange(event);
  };

  return (
    <Select
      {...restOrganizationField}
      onChange={handleChange}
      key={`${name}_input`}
      placeholder={t(label)}
      label={t(label)}
      disabled={disabled}
      size="small"
      error={Boolean(organizationMeta.touched && organizationMeta.error)}
      helperText={organizationMeta.touched && organizationMeta.error}
      className={classes.input}
      inputProps={{
        'aria-label': t(label)
      }}
    >
      {values &&
        values?.map(function OrganizationMenuItem(
          { label: optionLabel, value }: ClassroomDetailsOptionValues,
          key: number
        ) {
          return (
            <MenuItem key={`${key}`} value={value}>
              {optionLabel}
            </MenuItem>
          );
        })}
    </Select>
  );
};

const EducatorFieldValues = [
  { label: 'Educator 1', value: 1 },
  { label: 'Educator 2', value: 2 },
  { label: 'Educator 3', value: 3 }
];

export const PrimaryEducatorField = ({
  classes,
  name,
  t,
  disabled,
  label = 'Primary Educator',
  values = EducatorFieldValues
}: ClassroomDetailsFieldsProps): ReactElement => {
  const [field, meta] = useField(name);

  return (
    <Select
      {...field}
      key={`${field.name}_input`}
      placeholder={t(label)}
      label={t(label)}
      size="small"
      disabled={disabled}
      error={Boolean(meta.touched && meta.error)}
      helperText={meta.touched && meta.error}
      className={classes.input}
      inputProps={{
        'aria-label': t(label)
      }}
    >
      {values.map(({ label: optionLabel, value }: ClassroomDetailsOptionValues, key: number) => (
        <MenuItem key={`${key}`} value={value}>
          {optionLabel}
        </MenuItem>
      ))}
    </Select>
  );
};

// TODO
const SchoolYearFieldValues = [
  { value: 1, label: '2020 - 2021 A' },
  { value: 2, label: '2020 - 2021 B' },
  { value: 3, label: '2020 - 2021 C' }
];

export const SchoolYearField = ({
  classes,
  name,
  disabled,
  t,
  label = 'School Year/Term',
  values = SchoolYearFieldValues
}: ClassroomDetailsFieldsProps): ReactElement => (
  <Field name={name}>
    {({ field, meta }: FastFieldProps): ReactElement[] => [
      <Select
        {...field}
        key={`${field.name}_input`}
        placeholder={t(label)}
        label={t(label)}
        size="small"
        disabled={disabled}
        error={Boolean(meta.touched && meta.error)}
        helperText={meta.touched && meta.error}
        className={classes.input}
        inputProps={{
          'aria-label': t(label)
        }}
      >
        {values.map(({ label: optionLabel, value }: ClassroomDetailsOptionValues) => (
          <MenuItem key={`${optionLabel}`} value={value}>
            {optionLabel}
          </MenuItem>
        ))}
      </Select>
    ]}
  </Field>
);

export const CoEducatorField = ({
  name,
  classes,
  values: coeducatorOptions,
  t,
  disabled,
  label = 'Co-Educator(s)'
}: ClassroomDetailsFieldsProps & { values?: Array<CoEducator> }): ReactElement => {
  const [field] = useField(name);
  const { onChange, ...restField } = field;
  const [textValue, setTextValue] = React.useState('');
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event): void => {
    setTextValue(event.target.value);
  };

  const handleSelect = (event?: React.MouseEvent<HTMLLIElement>): void => {
    if (event) {
      const [id, email, firstName, lastName] = event.currentTarget.id.split('-');
      onChange({
        target: { value: [...field.value, { id: +id, firstName, lastName, email }], name }
      });
    }
  };

  const handleRemovalClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const filtered = field.value?.filter(({ id }: any) => {
      return id !== +event.currentTarget.id;
    });
    onChange({ target: { value: filtered, name } });
  };

  return (
    <Fragment>
      <SearchOrAdd
        idFn={({ id, firstName, lastName, email }: CoEducator): string =>
          [id, email, firstName, lastName].join('-')
        }
        listFn={(option: CoEducator): string =>
          `${option.firstName} ${option.lastName} (${option.email})`
        }
        id="co-educator-field"
        margin={false}
        clearOnSelect
        onSelect={handleSelect}
        options={coeducatorOptions || []}
        FormComponent={<InviteFormFragment />}
        submitButtonLabel={t('Invite Co-Educator')}
        cancelButtonLabel={t('Cancel')}
        openFormButtonLabel={
          <>
            {t("Can't find")} {<b>{textValue}</b>}
            {t('?')}
          </>
        }
        openFormButtonLabelAction={t('Click here to invite a new co-educator')}
        TextFieldProps={{
          ...restField,
          label: t(label),
          onChange: handleChange,
          name,
          size: 'small',
          fullWidth: true,
          disabled: disabled,
          placeholder: t('Start typing a name of an educator'),
          variant: 'outlined',
          inputProps: {
            'aria-label': `${t('Co-Educator')} ${1} ${t('Name')}`
          }
        }}
      />
      <div>
        {field.value &&
          field.value.map(({ id, firstName, lastName, email }: any) => {
            return (
              <div key={`selected-${id}`} className={classes.coeducatorContainer}>
                <div className={classes.coeducatorNameContainer}>
                  <Typography
                    className={classes.coeducatorName}
                  >{`${firstName} ${lastName} (${email})`}</Typography>
                </div>
                <div className={classes.coeducatorButtonContainer}>
                  <Button
                    variant="outlined"
                    id={id}
                    onClick={handleRemovalClick}
                    size="small"
                    startIcon={<CancelIcon />}
                    disableElevation
                    className={classes.coeducatorButton}
                  >
                    {t('Remove')}
                  </Button>
                </div>
              </div>
            );
          })}
      </div>
    </Fragment>
  );
};

export const SecretCodeTypeField = ({
  classes,
  name,
  t,
  disabled,
  label = 'Default Student Secret Code'
}: ClassroomDetailsFieldsProps): ReactElement => {
  return (
    <FastField name={name}>
      {({ field }: FastFieldProps): ReactElement[] => {
        return [
          <Typography key={`${field.name}_label`} variant="body1" className={classes.radioLabel}>
            {t(label)}
          </Typography>,
          <RadioGroup
            {...field}
            key={`${field.name}_input`}
            defaultValue="lettercards"
            className={classes.input}
          >
            <FormControlLabel
              classes={{ label: classes.radioText }}
              value={StudentLoginType.LETTER_CARDS}
              control={<Radio color="secondary" />}
              label={t('Letter Cards')}
              disabled={disabled}
            />
            <FormControlLabel
              classes={{ label: classes.radioText }}
              value={StudentLoginType.TEXTBOX}
              control={<Radio color="secondary" />}
              label={t('Text Box')}
              disabled={disabled}
            />
          </RadioGroup>
        ];
      }}
    </FastField>
  );
};

export const ClassroomColorField = ({
  classes,
  name,
  disabled,
  t,
  label = 'Class Color'
}: ClassroomDetailsFieldsProps): ReactElement => (
  <FastField name={name}>
    {({ field }: FastFieldProps): ReactElement[] => {
      const handleColorChange = (color: string): void => {
        field.onChange({
          target: { value: color, name: field.name }
        });
      };

      return [
        !disabled ? (
          <PopoverWrap
            title="Welcome"
            key={`${field.name}_label_popover`}
            description={
              'Assign a color to your class to differentiate it form the others. This will help you to find it faster!'
            }
            image={PopoverImg}
          >
            <Typography key={`${field.name}_label`} variant="body1" className={classes.inputLabel}>
              {t(label)}
            </Typography>
          </PopoverWrap>
        ) : (
          <div key={`${field.name}_label_popover`} />
        ),
        <ColorPicker
          {...field}
          disabled={disabled}
          key={`${field.name}_input`}
          onChange={handleColorChange}
          colors={colors}
        />
      ];
    }}
  </FastField>
);
